/**
 * Applies an AGNT page's SEO by feeding WordPress's Yoast payload
 * (`yoast_head_json`) into the app's shared `HeadData` head system, preserving
 * the legacy site's tuned SEO. Yoast supplies the title / description /
 * canonical / Open Graph; its JSON-LD `schema` graph is emitted verbatim
 * alongside HeadData's standard schema.
 *
 * Always renders one `HeadData` (so viewport + base tags are present even when a
 * page has no Yoast data); the AGNT route handlers set `routeName`, which
 * disables `_app`'s default `HeadData` so this is the only one. URLs are emitted
 * exactly as Yoast provides them.
 */

import Head from 'next/head';

import HeadData from 'components/head-data';

import type { HeadDataType } from 'components/head-data';
import type { YoastHeadJson } from 'utils/content-pages/yoast';

// Yoast's title/description carry HTML entities (`&amp;`, `&#8217;`); HeadData
// renders them as React text, so decode first or they'd double-encode.
function decodeEntities(value?: string): string | undefined {
  return value
    ?.replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, '\'')
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(parseInt(code, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, code: string) => String.fromCodePoint(parseInt(code, 16)));
}

// Yoast appends a trailing slash to canonical/og_url; strip it so all AGNT
// pages emit canonical URLs consistent with the Next.js route (no slash).
function stripTrailingSlash(url?: string): string | undefined {
  return url?.replace(/\/$/, '');
}

function toHeadData(seo: YoastHeadJson): HeadDataType {
  const robots = seo.robots ? Object.values(seo.robots) : [];
  return {
    title: decodeEntities(seo.title),
    metaDescription: decodeEntities(seo.description),
    canonicalUrl: stripTrailingSlash(seo.canonical),
    ogType: seo.og_type,
    ogUrl: stripTrailingSlash(seo.og_url),
    ogImage: seo.og_image?.[0]?.url,
    noIndex: robots.includes('noindex'),
    noFollow: robots.includes('nofollow'),
  };
}

export default function AgntSeo({ seo }: { seo?: YoastHeadJson | null }) {
  return (
    <>
      <HeadData data={seo ? toHeadData(seo) : undefined} />
      {seo?.schema ? (
        <Head>
          <script
            key="agnt-yoast-schema"
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(seo.schema) }}
          />
        </Head>
      ) : null}
    </>
  );
}
